import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import {
  getUruguayBank,
  stripAccountNumberSeparators,
  applyAccountNumberPadding,
  validateAccountNumber,
  validateHolderDocument,
  type UruguayBankAccountType,
} from "@gestionalo/shared-types";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BankCombobox } from "@/components/shared/BankCombobox";
import { cn } from "@/lib/utils";
import type { ProfessionalBankAccountDto } from "@/services/professionalEarningsService";

export interface BankAccountFormProps {
  initial: ProfessionalBankAccountDto | null;
  isSubmitting: boolean;
  onSubmit: (body: ProfessionalBankAccountDto) => void;
  onCancel?: () => void;
}

interface FormState {
  holderName: string;
  holderDocumentId: string;
  institutionCode: string;
  accountNumber: string;
  accountType: UruguayBankAccountType | "";
}

const EMPTY: FormState = {
  holderName: "",
  holderDocumentId: "",
  institutionCode: "",
  accountNumber: "",
  accountType: "",
};

export function BankAccountForm({
  initial,
  isSubmitting,
  onSubmit,
  onCancel,
}: BankAccountFormProps): JSX.Element {
  const [form, setForm] = useState<FormState>(EMPTY);
  const [touched, setTouched] = useState<Record<keyof FormState, boolean>>({
    holderName: false,
    holderDocumentId: false,
    institutionCode: false,
    accountNumber: false,
    accountType: false,
  });

  useEffect(() => {
    if (initial) {
      setForm({
        holderName: initial.holderName,
        holderDocumentId: initial.holderDocumentId,
        institutionCode: initial.institutionCode,
        accountNumber: initial.accountNumber,
        accountType: initial.accountType ?? "",
      });
    }
  }, [initial]);

  const selectedBank = useMemo(
    () => (form.institutionCode ? getUruguayBank(form.institutionCode) : null),
    [form.institutionCode],
  );

  const docValidation = validateHolderDocument(form.holderDocumentId);
  const accountValidation = selectedBank
    ? validateAccountNumber(selectedBank.code, form.accountNumber)
    : null;

  const errors = {
    holderName:
      form.holderName.trim().length < 3 ? "Nombre muy corto" : null,
    holderDocumentId: docValidation.ok
      ? null
      : form.holderDocumentId
        ? "Ingresá una CI (6–8 dígitos) o RUT (12 dígitos) válidos"
        : "Requerido",
    institutionCode: selectedBank ? null : "Elegí un banco",
    accountNumber: !selectedBank
      ? "Elegí un banco primero"
      : accountValidation?.ok
        ? null
        : form.accountNumber
          ? `Formato inválido. ${selectedBank.accountFormatHint}`
          : "Requerido",
    accountType:
      selectedBank && selectedBank.accountTypeApplies && !form.accountType
        ? "Elegí tipo de cuenta"
        : null,
  } as const;

  const isValid = Object.values(errors).every((e) => e == null);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setTouched({
      holderName: true,
      holderDocumentId: true,
      institutionCode: true,
      accountNumber: true,
      accountType: true,
    });
    if (!isValid || !selectedBank) return;
    const normalized = applyAccountNumberPadding(
      selectedBank,
      stripAccountNumberSeparators(form.accountNumber),
    );
    const accountType: UruguayBankAccountType = selectedBank.accountTypeApplies
      ? (form.accountType as UruguayBankAccountType)
      : "savings";
    onSubmit({
      holderName: form.holderName.trim(),
      holderDocumentId: docValidation.ok
        ? docValidation.normalized
        : form.holderDocumentId.trim(),
      institutionCode: selectedBank.code,
      accountNumber: normalized,
      accountType,
    });
  };

  return (
    <form className="grid gap-4 sm:grid-cols-2" onSubmit={handleSubmit}>
      <div className="space-y-2">
        <Label htmlFor="holderName">Titular</Label>
        <Input
          id="holderName"
          value={form.holderName}
          onChange={(e) =>
            setForm((f) => ({ ...f, holderName: e.target.value }))
          }
          onBlur={() => setTouched((t) => ({ ...t, holderName: true }))}
          autoComplete="name"
          aria-invalid={touched.holderName && errors.holderName != null}
        />
        {touched.holderName && errors.holderName ? (
          <p className="text-xs text-destructive">{errors.holderName}</p>
        ) : null}
      </div>

      <div className="space-y-2">
        <Label htmlFor="holderDocumentId">Documento del titular</Label>
        <Input
          id="holderDocumentId"
          value={form.holderDocumentId}
          inputMode="numeric"
          onChange={(e) =>
            setForm((f) => ({ ...f, holderDocumentId: e.target.value }))
          }
          onBlur={() =>
            setTouched((t) => ({ ...t, holderDocumentId: true }))
          }
          placeholder="CI o RUT"
          aria-invalid={
            touched.holderDocumentId && errors.holderDocumentId != null
          }
        />
        {touched.holderDocumentId && errors.holderDocumentId ? (
          <p className="text-xs text-destructive">{errors.holderDocumentId}</p>
        ) : null}
      </div>

      <div className="space-y-2">
        <Label htmlFor="institutionCode">Banco</Label>
        <BankCombobox
          id="institutionCode"
          value={form.institutionCode}
          onChange={(code) => {
            setForm((f) => ({ ...f, institutionCode: code }));
            setTouched((t) => ({ ...t, institutionCode: true }));
          }}
          ariaInvalid={touched.institutionCode && errors.institutionCode != null}
        />
        {touched.institutionCode && errors.institutionCode ? (
          <p className="text-xs text-destructive">{errors.institutionCode}</p>
        ) : null}
      </div>

      <div className="space-y-2">
        <Label htmlFor="accountNumber">Número de cuenta</Label>
        <Input
          id="accountNumber"
          inputMode="numeric"
          value={form.accountNumber}
          onChange={(e) =>
            setForm((f) => ({ ...f, accountNumber: e.target.value }))
          }
          onBlur={() => {
            setTouched((t) => ({ ...t, accountNumber: true }));
            if (selectedBank) {
              const padded = applyAccountNumberPadding(
                selectedBank,
                stripAccountNumberSeparators(form.accountNumber),
              );
              if (padded !== form.accountNumber) {
                setForm((f) => ({ ...f, accountNumber: padded }));
              }
            }
          }}
          placeholder={selectedBank?.accountFormatHint ?? "Elegí un banco primero"}
          disabled={!selectedBank}
          aria-invalid={touched.accountNumber && errors.accountNumber != null}
        />
        <p
          className={cn(
            "text-xs",
            touched.accountNumber && errors.accountNumber
              ? "text-destructive"
              : "text-muted-foreground",
          )}
        >
          {touched.accountNumber && errors.accountNumber
            ? errors.accountNumber
            : selectedBank?.accountFormatHint ?? "Sin espacios ni guiones"}
        </p>
      </div>

      {selectedBank?.accountTypeApplies ? (
        <div className="space-y-2 sm:col-span-2">
          <Label className="block">Tipo de cuenta</Label>
          <div
            role="radiogroup"
            aria-label="Tipo de cuenta"
            className="grid max-w-xl grid-cols-2 gap-3"
          >
            {(
              [
                [
                  "savings",
                  "Caja de ahorro",
                  "Para uso personal, sin cheques",
                ],
                [
                  "checking",
                  "Cuenta corriente",
                  "Habilita cheques y giros",
                ],
              ] as const
            ).map(([val, label, description]) => {
              const selected = form.accountType === val;
              return (
                <button
                  key={val}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  className={cn(
                    "group relative flex flex-col items-start gap-1 rounded-lg border p-4 text-left transition-all",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                    selected
                      ? "border-primary bg-primary/5 ring-1 ring-primary"
                      : "border-border bg-background hover:border-muted-foreground/40 hover:bg-muted/40",
                  )}
                  onClick={() => {
                    setForm((f) => ({ ...f, accountType: val }));
                    setTouched((t) => ({ ...t, accountType: true }));
                  }}
                >
                  <span
                    className={cn(
                      "flex h-4 w-4 items-center justify-center rounded-full border transition-colors",
                      selected
                        ? "border-primary bg-primary"
                        : "border-muted-foreground/40 bg-transparent",
                    )}
                    aria-hidden
                  >
                    {selected ? (
                      <span className="h-1.5 w-1.5 rounded-full bg-primary-foreground" />
                    ) : null}
                  </span>
                  <span
                    className={cn(
                      "text-sm font-medium",
                      selected ? "text-foreground" : "text-foreground/90",
                    )}
                  >
                    {label}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {description}
                  </span>
                </button>
              );
            })}
          </div>
          {touched.accountType && errors.accountType ? (
            <p className="text-xs text-destructive">{errors.accountType}</p>
          ) : null}
        </div>
      ) : null}

      <div className="flex items-center justify-end gap-3 sm:col-span-2">
        {onCancel ? (
          <Button
            type="button"
            variant="ghost"
            disabled={isSubmitting}
            onClick={onCancel}
          >
            Cancelar
          </Button>
        ) : null}
        <Button
          type="submit"
          disabled={isSubmitting || !isValid}
          className="transition-colors duration-150"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
              Guardando…
            </>
          ) : (
            "Guardar"
          )}
        </Button>
      </div>
    </form>
  );
}
